import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { SOLVE_LEVELS, normalizeSolveLevelId } from '../utils/solveLevels'

/**
 * 풀이 레벨 드롭다운.
 * 네이티브 <select> 대체 — 메뉴 안 스크롤·클릭이 끊기지 않게 한다.
 */
export default function LevelSelect({
  value,
  onChange,
  title = '백병동 편·장 누적. 예: 1편 3장=속7까지, 부속화음은 2편 1장부터.',
}) {
  const levelId = normalizeSolveLevelId(value)
  const current = SOLVE_LEVELS.find((l) => l.id === levelId) ?? SOLVE_LEVELS[0]
  const [open, setOpen] = useState(false)
  const btnRef = useRef(null)
  const menuRef = useRef(null)
  const [pos, setPos] = useState({ top: 0, left: 0, width: 220, maxHeight: 280 })

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return undefined
    const place = () => {
      const r = btnRef.current.getBoundingClientRect()
      const width = Math.max(r.width, 200)
      const spaceBelow = window.innerHeight - r.bottom - 8
      const spaceAbove = r.top - 8
      const openUp = spaceBelow < 180 && spaceAbove > spaceBelow
      const maxHeight = Math.min(280, Math.max(140, openUp ? spaceAbove : spaceBelow))
      setPos({
        // 트리거와 붙여서 마우스가 틈으로 빠지지 않게
        top: openUp ? Math.max(8, r.top - maxHeight) : r.bottom,
        left: Math.min(r.left, window.innerWidth - width - 8),
        width,
        maxHeight,
      })
    }
    place()
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    const onPointerDown = (e) => {
      if (btnRef.current?.contains(e.target)) return
      if (menuRef.current?.contains(e.target)) return
      setOpen(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    // 메뉴 내부 스크롤은 무시 — 바깥(페이지) 스크롤만 닫기
    const onScroll = (e) => {
      if (menuRef.current?.contains(e.target)) return
      setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [open])

  const pick = (id) => {
    onChange?.(id)
    setOpen(false)
  }

  return (
    <>
      <button
        type="button"
        ref={btnRef}
        className={`tb-level-trigger${open ? ' is-open' : ''}`}
        title={title}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="tb-level-trigger-label">{current.label}</span>
        <span className="tb-level-trigger-caret" aria-hidden="true">▾</span>
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          className="tb-level-menu"
          role="listbox"
          aria-label="레벨 설정"
          style={{
            top: pos.top,
            left: pos.left,
            width: pos.width,
            maxHeight: pos.maxHeight,
          }}
        >
          {SOLVE_LEVELS.map((lv) => {
            const selected = lv.id === current.id
            return (
              <button
                key={lv.id}
                type="button"
                role="option"
                aria-selected={selected}
                className={`tb-level-option${selected ? ' is-selected' : ''}`}
                title={lv.title}
                onPointerDown={(e) => {
                  // pointerdown 단계에서 바로 선택 — 바깥 닫기 핸들러와 경쟁하지 않음
                  e.preventDefault()
                  e.stopPropagation()
                  pick(lv.id)
                }}
              >
                {lv.label}
              </button>
            )
          })}
        </div>,
        document.body,
      )}
    </>
  )
}
